import type {
	AdminQueueRow,
	AdminRequestCounts,
	AdminRequestDetail,
	ApproveRequestBody,
	ListAdminRequestsParams,
	ReviewCommentBody,
} from "@/features/applications/api/types";
import { api } from "@/shared/api/client";
import type { InfinityPage } from "@/shared/api/types";

export function toSearchParams(
	params: ListAdminRequestsParams,
): URLSearchParams {
	const search = new URLSearchParams();
	search.set("type", params.type);
	if (params.status?.length) {
		for (const status of params.status) {
			search.append("status", status);
		}
	}
	if (params.search && params.search.trim().length >= 3) {
		search.set("search", params.search.trim());
	}
	if (params.sort) {
		search.set("sort", params.sort);
	}
	if (params.order) {
		search.set("order", params.order);
	}
	search.set("page", String(params.page ?? 1));
	search.set("limit", String(params.limit ?? 20));
	return search;
}

export async function listAdminRequests(
	params: ListAdminRequestsParams,
): Promise<InfinityPage<AdminQueueRow>> {
	return api
		.get("admin/requests", { searchParams: toSearchParams(params) })
		.json();
}

export async function getAdminRequestCounts(): Promise<AdminRequestCounts> {
	return api.get("admin/requests/counts").json();
}

export async function getAdminRequest(id: string): Promise<AdminRequestDetail> {
	return api.get(`admin/requests/${id}`).json();
}

export async function approveAdminRequest(
	id: string,
	body: ApproveRequestBody = {},
): Promise<AdminRequestDetail> {
	return api.post(`admin/requests/${id}/approve`, { json: body }).json();
}

export async function declineAdminRequest(
	id: string,
	body: ReviewCommentBody,
): Promise<AdminRequestDetail> {
	return api.post(`admin/requests/${id}/decline`, { json: body }).json();
}

export async function reopenAdminRequest(
	id: string,
	body: ReviewCommentBody,
): Promise<AdminRequestDetail> {
	return api.post(`admin/requests/${id}/reopen`, { json: body }).json();
}

export const adminRequestKeys = {
	all: ["admin-requests"] as const,
	counts: ["admin-requests-counts"] as const,
	list: (params: ListAdminRequestsParams) =>
		["admin-requests", "list", params] as const,
	detail: (id: string) => ["admin-requests", "detail", id] as const,
};
